import { Spin, Tag, Tooltip } from 'antd'
import ReactCountUp from 'components/ReactCountUp'
import {
  IndicationSet,
  RD_TYPE_INDICATION,
  RD_TYPE_INDICATION_SET,
} from 'features/indicationTriage'
import { useRolodexHistogram } from 'features/rolodex'
import flatten from 'lodash/flatten'
import reduce from 'lodash/reduce'
import sum from 'lodash/sum'
import uniqBy from 'lodash/uniqBy'
import React, { FC, useMemo } from 'react'
import styled from 'styled-components'
import { GeckoTheme } from 'styles/GeckoTheme'
import { RolodexEntryType } from 'types'

const IndicationCountDisplayContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
`

const TagContentContainer = styled.div`
  display: flex;
  justify-content: center;
`

type IndicationGroup = {
  key: string
  color?: string
  tooltip?: string
  count: number
}

const RawIndicationGroupCountDisplay: FC<{
  groups: ReadonlyArray<IndicationGroup>
}> = ({ groups }) => {
  return (
    <IndicationCountDisplayContainer>
      {groups.map(({ key, color, tooltip, count }) => (
        <Tooltip key={key} title={tooltip}>
          <Tag
            color={color ?? GeckoTheme.colors.LimeGreen}
            style={{ width: '2rem' }}>
            <TagContentContainer>
              <ReactCountUp start={0} end={count} duration={1} />
            </TagContentContainer>
          </Tag>
        </Tooltip>
      ))}
    </IndicationCountDisplayContainer>
  )
}

const IndicationGroupCountDisplay = React.memo(RawIndicationGroupCountDisplay)

function unmatchedGroup(count: number): IndicationGroup {
  return {
    key: '@@unmatched@@',
    color: GeckoTheme.colors.gray[600],
    tooltip: 'Classificaiton not matching indication set',
    count,
  }
}

export interface IndicationGroupDisplayProps {
  indicationSets: ReadonlyArray<IndicationSet>
}

/**
 * Displays a tag-style display count of different display classifications.
 * Keyed on 'indicationSetType:classification'.
 * For indications without a classificaiton, a default is applied.
 */
export const IndicationClassificationCountDisplay: FC<
  IndicationGroupDisplayProps
> = ({ indicationSets }) => {
  const { data: histoData, isLoading } = useRolodexHistogram(
    useMemo(
      () => ({
        type: RD_TYPE_INDICATION,
        select: [
          {
            label: 'iSetType',
            type: RD_TYPE_INDICATION_SET,
            key: 'indication_set_type',
            group_by: true,
          },
          {
            label: 'classification',
            key: 'classificationInfo.classification',
            type: RD_TYPE_INDICATION,
            group_by: true,
          },
          {
            label: 'count',
            type: RolodexEntryType.INDICATION,
            field: 'id',
            op: 'count',
          },
        ],
        tags: {
          [RD_TYPE_INDICATION_SET]: indicationSets.map((iSet) => iSet.id),
        },
        links: [RD_TYPE_INDICATION_SET],
        limit: 100000,
      }),
      [indicationSets]
    )
  )
  const histoEntries = useMemo(
    () =>
      flatten(histoData).map((histoEntry) => ({
        group: `${histoEntry['iSetType']}:${histoEntry['classification']}`,
        count: histoEntry['count'] as number,
      })),
    [histoData]
  )

  const groupCounts: Map<string, number> = useMemo(
    () =>
      reduce(
        histoEntries,
        (accum, { group, count: entryCount }) => {
          const accumCount = accum.get(group) ?? 0
          const newCount = accumCount + entryCount
          return new Map(accum).set(group, newCount)
        },
        new Map<string, number>()
      ),
    [histoEntries]
  )

  const groups = useMemo<ReadonlyArray<IndicationGroup>>(
    () =>
      uniqBy(
        indicationSets.flatMap((iSet) => {
          const classifications =
            iSet.classificationConfig?.[0]?.classifications ?? {}
          return Object.entries(classifications).map(([classKey, classDef]) => {
            const group = `${iSet.indicationSetType ?? null}:${classKey}`
            return {
              key: group,
              count: groupCounts.get(group) ?? 0,
              color: classDef.color,
              tooltip: `${classDef.description ?? classKey}`,
            }
          })
        }),
        ({ key }) => key
      ),
    [groupCounts, indicationSets]
  )

  const unmatchedCount = useMemo(() => {
    const groupKeys = new Set(groups.map(({ key }) => key))
    return sum(
      [...groupCounts.entries()].map(([key, count]) =>
        groupKeys.has(key) ? 0 : count
      )
    )
  }, [groupCounts, groups])

  const groupsWithUnmatched = useMemo(
    () => [
      ...groups,
      ...(unmatchedCount ? [unmatchedGroup(unmatchedCount)] : []),
    ],
    [groups, unmatchedCount]
  )

  return (
    <Spin spinning={isLoading} size='small'>
      <IndicationGroupCountDisplay groups={groupsWithUnmatched} />
    </Spin>
  )
}
